import React, { useState } from "react";
import ContentWrapper from "../Layout/ContentWrapper";
import {
  Button,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import config from "../../config";
import { setAuthToken } from "../../utils/authToken";

const SigninRedirect = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [devToken, setDevToken] = useState("");
  const [devUserId, setDevUserId] = useState("");

  // Dev login option 1 — by token: store the pasted token, then let App's
  // normal bootstrap (fetchUser -> /api/status) run against it. Validates
  // against Spotlight, so it needs a token valid for this environment.
  const handleDevTokenLogin = () => {
    const token = devToken.trim();
    if (!token) return;
    setAuthToken(token, true);
    window.location.reload();
  };

  // Dev login option 2 — by user_id: navigate with ?dev_user_id=<n>, which App
  // picks up to call /api/status directly, skipping Spotlight entirely. Fastest
  // way to "become" a test user; no token needed.
  const handleDevUserLogin = () => {
    const id = devUserId.trim();
    if (!id) return;
    const params = new URLSearchParams(window.location.search);
    params.set("dev_user_id", id);
    if (!params.get("contest_id")) params.set("contest_id", "44");
    window.location.assign(`${window.location.pathname}?${params.toString()}`);
  };

  // The Lobby owns authentication for embedded (production) builds. When the
  // app is unauthed we render nothing so the Lobby can take over, instead of
  // showing an "Authentication Failed" dialog inside the webview. The dev-login
  // helper below still renders on dev / Cloud Run preview hosts, which set
  // config.allowDevLogin.
  if (!config.allowDevLogin) {
    return null;
  }

  return (
    <ContentWrapper>
      <Grid
        container
        sx={{
          width: "100vw",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          container
          direction="column"
          sx={[
            {
              padding: "24px",
              gap: "16px",
              backgroundColor: theme.branding.gray[0],
              borderRadius: "14px",
              border: `2px solid ${theme.branding.gray[200]}`,
              boxShadow: `0px 3px 7px -3px ${theme.branding.gray[200]}`,
              justifyContent: "center",
              alignItems: "center",
            },
            (theme) =>
              theme.applyStyles({
                backgroundColor: theme.branding.darkBlue[800],
                borderColor: theme.branding.darkBlue[600],
              }),
          ]}
        >
          <Grid
            container
            direction="column"
            sx={{
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              gap: "4px",
            }}
          >
            <Typography variant="h4">Authentication Failed</Typography>
            <Typography variant="body2">
              Please return to Spotlight to sign in to your account.
            </Typography>
          </Grid>
          <Button
            onClick={() => {
              window.open("https://my.spotlightnews.us/contests");
            }}
            fullWidth={isMobile}
            sx={{ padding: "8px 16px" }}
          >
            Sign In →
          </Button>

          {/* DEV ONLY — never renders in staging/production (see config.allowDevLogin). */}
          {config.allowDevLogin && (
            <Grid
              container
              direction="column"
              sx={{
                width: "100%",
                gap: "8px",
                marginTop: "8px",
                paddingTop: "16px",
                borderTop: `1px dashed ${theme.branding.gray[200]}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ textAlign: "center", opacity: 0.7 }}
              >
                Dev login (development only)
              </Typography>

              {/* Option 1: paste a dev JWT (real flow, validates vs Spotlight) */}
              <TextField
                value={devToken}
                onChange={(e) => setDevToken(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleDevTokenLogin();
                }}
                placeholder="Paste a dev JWT (eyJhbGci…)"
                size="small"
                fullWidth
              />
              <Button
                onClick={handleDevTokenLogin}
                disabled={!devToken.trim()}
                variant="outlined"
                fullWidth
                sx={{ padding: "8px 16px" }}
              >
                Log in with token →
              </Button>

              {/* Option 2: by user_id (skips Spotlight entirely) */}
              <TextField
                value={devUserId}
                onChange={(e) => setDevUserId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleDevUserLogin();
                }}
                placeholder="…or a user_id (e.g. 3)"
                size="small"
                fullWidth
              />
              <Button
                onClick={handleDevUserLogin}
                disabled={!devUserId.trim()}
                variant="outlined"
                fullWidth
                sx={{ padding: "8px 16px" }}
              >
                Log in as user →
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </ContentWrapper>
  );
};

export default SigninRedirect;
